import { Box, Divider, styled, IconButton, Collapse } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import Heading, { HeadingProps } from 'components/Heading';
import Spacing from 'components/Spacing';
import React from 'react';
import { Row } from 'components/Layout';

const SectionDivider = styled(Divider)(({ theme }) => ({
  marginBottom: theme.spacing(6),
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  padding: theme.spacing(0),
  display: 'flex',
  alignItems: 'flex-start',
  alignSelf: 'flex-start',
  color: theme.palette.text.rootColor,
}));

const StyledRow = styled(Row, {
  shouldForwardProp: (prop) => prop !== 'collapsible',
})<{ collapsible?: boolean }>(({ collapsible }) => ({
  cursor: collapsible ? 'pointer' : 'default',
}));

const CollapsibleContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})<{ isExpanded?: boolean }>(({ theme, isExpanded }) => ({
  transition: `margin ${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut}`,
  margin: isExpanded ? undefined : '-1rem 0',
  overflow: 'hidden',
}));

const HeaderContentWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'withSpacing',
})<{ withSpacing?: boolean }>(({ theme, withSpacing }) => ({
  marginBottom: withSpacing ? 0 : theme.spacing(3),
  width: '100%',
}));

type BasePageSectionProps = {
  showDivider?: boolean;
  tooltip?: React.ReactNode;
  variant?: HeadingProps['variant'];
  withSpacing?: boolean;
  defaultExpanded?: boolean;
  collapsible?: boolean;
  children?: React.ReactNode;
};

type WithTitleProps = BasePageSectionProps & {
  title?: string;
  headerContent?: never;
};

type WithHeaderContentProps = BasePageSectionProps & {
  headerContent?: React.ReactNode;
  title?: never;
};

type PageSectionProps = WithTitleProps | WithHeaderContentProps;

const PageSection: React.FC<PageSectionProps> = ({
  variant = 'h2',
  showDivider,
  title,
  headerContent,
  tooltip,
  children,
  withSpacing,
  defaultExpanded = true,
  collapsible = false,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);
  const SectionComponent = withSpacing ? Spacing : React.Fragment;

  return (
    <Box>
      {showDivider && <SectionDivider />}
      <SectionComponent>
        {(!!title || !!headerContent) && (
          <StyledRow
            justify="space-between"
            collapsible={collapsible}
            onClick={() => collapsible && setIsExpanded(!isExpanded)}
          >
            {headerContent ? (
              <HeaderContentWrapper withSpacing={withSpacing}>
                {headerContent}
              </HeaderContentWrapper>
            ) : (
              <Heading
                tooltip={tooltip}
                variant={variant}
                noMargin={withSpacing}
              >
                {title}
              </Heading>
            )}
            {collapsible && (
              <StyledIconButton size="small">
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </StyledIconButton>
            )}
          </StyledRow>
        )}
        {collapsible ? (
          <CollapsibleContent isExpanded={isExpanded}>
            <Collapse in={isExpanded} timeout={300}>
              {children}
            </Collapse>
          </CollapsibleContent>
        ) : (
          children
        )}
      </SectionComponent>
    </Box>
  );
};

export default PageSection;
